import { TROPHY_ICON } from "./nav-config";
import { ThemeToggle } from "./theme-toggle";
import { Button } from "@/components/ui/primitives";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { cn } from "@/lib/cn";

const LINKS = [
  { href: "#impact", label: "Our impact" },
  { href: "#mission", label: "Our mission" },
  { href: "#experience", label: "How it works" },
  { href: "#inclusion", label: "Inclusion" },
];

/** Public header for anonymous visitors — transparent over the hero, solid on scroll. */
export function MarketingHeader() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);

  // Only the landing page renders a dark hero behind the header; everywhere
  // else the header must stay solid or its light-on-light text is invisible.
  const overHero = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const transparent = overHero && !scrolled;

  return (
    <header
      className={cn(
        "sticky top-0 z-40 transition-all duration-300",
        transparent
          ? "border-b border-transparent bg-transparent"
          : "glass border-b border-border shadow-[var(--shadow-sm)]",
      )}
    >
      <div className="mx-auto flex h-16 w-full max-w-[var(--container)] items-center gap-6 px-4 sm:px-8 lg:px-12">
        <Link to="/" className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--brand-accent)] text-white shadow-[var(--shadow-accent)]">
            <TROPHY_ICON className="h-[18px] w-[18px]" />
          </span>
          <span
            className={cn(
              "text-[15px] font-extrabold tracking-tight transition-colors",
              transparent ? "text-white" : "text-text",
            )}
          >
            STEP<span className="text-[var(--brand-accent)]">-</span>TEMBER
          </span>
        </Link>

        <nav
          aria-label="Sections"
          className="ml-4 hidden items-center gap-1 md:flex"
        >
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={overHero ? l.href : `/${l.href}`}
              className={cn(
                "rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                transparent
                  ? "text-white/75 hover:bg-white/10 hover:text-white"
                  : "text-text-muted hover:bg-surface-muted hover:text-text",
              )}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <span className={cn(transparent && "text-white")}>
            <ThemeToggle />
          </span>
          {pathname !== "/signin" && (
            <Link to="/signin">
              <Button size="sm" className="shadow-[var(--shadow-accent)]">
                Sign in
              </Button>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
